import { useState, useMemo, useEffect, useCallback } from 'react';
import { useEmployees } from '../../hooks/useEmployees';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import { useTimesheets, saveTimesheet } from '../../hooks/useTimesheets';
import { timesheetsService } from '../../services';
import { useToast } from '../../contexts/ToastContext';
import { OffCrewType } from '../../types';
import { monthToApiDate, OffCrewRow } from './timesheetUtils';
import type { TimesheetRow } from './TimesheetGrid';

export const ALL_VESSELS = 'all';

const branchMatches = (branch: any, value: any): boolean => {
  if (!branch || value === undefined || value === null || value === '') return false;
  return (
    (branch.documentId && String(value) === String(branch.documentId)) ||
    String(value) === String(branch.id)
  );
};

/** Loads employees + saved timesheets for a vessel/month and manages the grid state */
export function useTimesheetState(vesselId: string, month: string, branches: any[]) {
  const { showToast } = useToast();

  const employeesQO = useMemo(() => ({
    pagination: { pageSize: 500 },
    sort: [{ field: 'name', order: 'asc' as const }],
  }), []);
  const { data: employees = [], isLoading: employeesLoading, error, refetch } = useEmployees(employeesQO);

  const crewQO = useMemo(() => ({ pagination: { pageSize: 500 } }), []);
  const { data: crewChanges = [] } = useCrewChanges(crewQO);

  const timesheetQO = useMemo(() => ({
    filters: { month: { $eq: monthToApiDate(month) } },
    pagination: { pageSize: 500 },
  }), [month]);
  const { data: timesheets = [], refetch: refetchTimesheets } = useTimesheets(timesheetQO);

  const selectedBranch = useMemo(
    () => branches.find((b: any) => (b.documentId || String(b.id)) === vesselId),
    [branches, vesselId]
  );

  // Latest crew change per employee ties them to that vessel for the timesheet.
  // Sign-off is included too: an employee who signed off mid-month still needs
  // their worked days recorded on that vessel's sheet.
  const activeAssignments = useMemo(() => {
    const latest: Record<string, any> = {};
    (crewChanges as any[]).forEach((c: any) => {
      const empKey = String(c.employeeId ?? '');
      if (!empKey) return;
      if (!latest[empKey] || new Date(c.createdAt || 0) > new Date(latest[empKey].createdAt || 0)) {
        latest[empKey] = c;
      }
    });
    const map: Record<string, string> = {};
    Object.values(latest).forEach((c: any) => {
      if (c.status === 'sign_on' || c.status === 'continue' || c.status === 'sign_off') {
        map[String(c.employeeId)] = String(c.branchId ?? '');
      }
    });
    return map;
  }, [crewChanges]);

  const assignmentOf = useCallback((emp: any): string | undefined =>
    activeAssignments[String(emp.documentId)] ?? activeAssignments[String(emp.id)],
    [activeAssignments]
  );

  // An employee belongs to the vessel if their record's branch matches OR their
  // latest crew change (sign on / continue) puts them on it
  const vesselEmployees = useMemo(() => {
    if (vesselId === ALL_VESSELS) return employees as any[];
    if (!selectedBranch) return [];
    return (employees as any[]).filter((e: any) =>
      branchMatches(selectedBranch, e.branchId) ||
      branchMatches(selectedBranch, assignmentOf(e))
    );
  }, [employees, selectedBranch, vesselId, assignmentOf]);

  const resolveEmployeeBranchDocId = useCallback((emp: any): string => {
    const raw = assignmentOf(emp) || emp.branchId;
    const branch = branches.find((b: any) => branchMatches(b, raw));
    return branch?.documentId || String(branch?.id || raw || '');
  }, [assignmentOf, branches]);

  const [rows, setRows] = useState<Record<string, TimesheetRow>>({});
  const [offCrewRows, setOffCrewRows] = useState<OffCrewRow[]>([]);

  // Load saved records into the grid whenever vessel/month/data changes
  useEffect(() => {
    const next: Record<string, TimesheetRow> = {};
    const off: OffCrewRow[] = [];
    const matchesVessel = (t: any) => !selectedBranch || branchMatches(selectedBranch, t.branchId);

    (timesheets as any[]).forEach((t: any) => {
      const empKey = t.employeeId || t.employee?.documentId;
      const days = t.days && typeof t.days === 'object' ? t.days : {};
      if (empKey) {
        next[empKey] = { documentId: t.documentId, id: t.id, days, description: t.description || '' };
      } else if ((t.personType || t.personName) && matchesVessel(t)) {
        off.push({
          key: t.documentId || `saved-${t.id}`,
          documentId: t.documentId,
          id: t.id,
          type: (t.personType as OffCrewType) || 'visitor',
          name: t.personName || '',
          days,
          description: t.description || '',
        });
      }
    });
    setRows(next);
    setOffCrewRows(off);
  }, [timesheets, selectedBranch]);

  const handleRowChange = useCallback((employeeKey: string, row: TimesheetRow) => {
    setRows((prev) => ({ ...prev, [employeeKey]: row }));
  }, []);

  const handleOffCrewChange = useCallback((key: string, updated: OffCrewRow) => {
    setOffCrewRows((prev) => prev.map((r) => (r.key === key ? updated : r)));
  }, []);

  const addOffCrewRow = useCallback((type: OffCrewType) => {
    setOffCrewRows((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${prev.length}`, type, name: '', days: {}, description: '' },
    ]);
  }, []);

  const removeOffCrewRow = useCallback(async (key: string) => {
    const row = offCrewRows.find((r) => r.key === key);
    if (row?.documentId) {
      if (!window.confirm('Delete this saved off-crew row?')) return;
      try {
        await timesheetsService.delete(row.documentId);
      } catch (e) {
        console.error('Failed to delete off-crew row:', e);
      }
    }
    setOffCrewRows((prev) => prev.filter((r) => r.key !== key));
  }, [offCrewRows]);

  const [isSaving, setIsSaving] = useState(false);
  const save = useCallback(async () => {
    if (!vesselId) return;
    setIsSaving(true);
    try {
      let count = 0;
      for (const emp of vesselEmployees) {
        const key = emp.documentId || String(emp.id);
        const row = rows[key];
        if (!row || (Object.keys(row.days).length === 0 && !row.description && !row.documentId)) continue;
        const branchId = vesselId === ALL_VESSELS ? resolveEmployeeBranchDocId(emp) : vesselId;
        await saveTimesheet({
          documentId: row.documentId,
          id: row.id,
          employeeId: key,
          branchId,
          month: monthToApiDate(month),
          days: row.days,
          description: row.description,
        });
        count++;
      }
      if (vesselId !== ALL_VESSELS) {
        for (const row of offCrewRows) {
          if (!row.name && Object.keys(row.days).length === 0 && !row.documentId) continue;
          await saveTimesheet({
            documentId: row.documentId,
            id: row.id,
            branchId: vesselId,
            month: monthToApiDate(month),
            days: row.days,
            description: row.description,
            personType: row.type,
            personName: row.name,
          });
          count++;
        }
      }
      showToast(`Timesheet saved (${count} row${count === 1 ? '' : 's'})`, 'success');
      refetchTimesheets();
    } catch (e: any) {
      console.error('Timesheet save failed:', e);
      showToast(e?.error?.message || e?.message || 'Failed to save timesheet', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [vesselId, month, rows, offCrewRows, vesselEmployees, resolveEmployeeBranchDocId, refetchTimesheets, showToast]);

  return {
    employeesLoading, error, refetch,
    selectedBranch, vesselEmployees,
    rows, handleRowChange,
    offCrewRows, handleOffCrewChange, addOffCrewRow, removeOffCrewRow,
    save, isSaving,
  };
}
